import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/Button';
import { ArrowRight } from 'lucide-react';
import Footer from '../components/Footer';

import HeroSection from '../components/landing/sections/HeroSection';
import PricingSection from '../components/landing/sections/PricingSection';
import CompareSection from '../components/landing/sections/CompareSection';
import FaqSection from '../components/landing/sections/FaqSection';
import LandingContainer from '../components/landing/LandingContainer';

import {
  HOW_IT_WORKS_STEPS,
  FEATURES,
  SEO_STRUCTURED_DATA
} from '../data/landingPageData';
import { geoConfig } from '../utils/geoConfig';
import { seoConfig } from '../utils/seoConfig';
import {
  collectStructuredData,
  generateFAQSchema,
  generateWebSiteSchema,
  LLMS_TXT_URL,
} from '../utils/geoUtils';

// Reusable components

const LANDING_GEO_SCHEMA = collectStructuredData(SEO_STRUCTURED_DATA, [
  generateWebSiteSchema(),
  generateFAQSchema(geoConfig.homepageFaq),
]);

export default function LandingPage() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoConfig.pages.home.title}</title>
        <meta name="title" content={seoConfig.pages.home.title} />
        <meta name="description" content={seoConfig.pages.home.description} />
        <meta name="keywords" content={seoConfig.pages.home.keywords} />
        <meta name="author" content="FormCare" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://formcare.io/" />
        <meta property="og:title" content={seoConfig.pages.home.title} />
        <meta property="og:description" content={seoConfig.pages.home.description} />
        <meta property="og:image" content="https://formcare.io/og.jpg" />
        <meta property="og:image:alt" content="Paste one URL into your HTML form. 150 free submissions a month, no credit card." />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="FormCare" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://formcare.io/" />
        <meta property="twitter:title" content={seoConfig.pages.home.title} />
        <meta property="twitter:description" content={seoConfig.pages.home.description} />
        <meta property="twitter:image" content="https://formcare.io/og.jpg" />
        <meta property="twitter:image:alt" content="Paste one URL into your HTML form. 150 free submissions a month, no credit card." />
        <meta property="twitter:creator" content="@formcare" />

        {/* Additional Meta Tags */}
        <meta name="theme-color" content="#4A90E2" />
        <meta name="msapplication-TileColor" content="#4A90E2" />
        <meta name="msapplication-config" content="/browserconfig.xml" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://formcare.io/" />

        <link rel="alternate" type="text/plain" href={LLMS_TXT_URL} title="LLM-readable site summary" />

        {/* Favicon */}
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/site.webmanifest" />

        {/* Structured Data (SEO + GEO) */}
        <script type="application/ld+json">
          {JSON.stringify(LANDING_GEO_SCHEMA)}
        </script>

        {/* Preconnect to external domains */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      </Helmet>

      {/* Hero Section */}
      <HeroSection />

      <section className="border-y border-neutral-200 bg-neutral-50 py-10 sm:py-16">
        <LandingContainer>
          <h2 className="max-w-xl font-heading text-2xl font-bold leading-tight tracking-tight text-charcoal-900 sm:text-4xl">
            Three steps. Your form stays as it is.
          </h2>
          <ol className="mt-8 grid gap-8 sm:mt-10 lg:grid-cols-3">
            {HOW_IT_WORKS_STEPS.map((step, index) => (
              <li key={step.step} className="relative min-w-0">
                {index < HOW_IT_WORKS_STEPS.length - 1 && (
                  <span
                    className="absolute left-10 top-5 hidden h-px w-[calc(100%+2rem-2.5rem)] bg-neutral-300 lg:block"
                    aria-hidden
                  />
                )}
                <div className="relative z-10 flex h-10 w-10 items-center justify-center rounded-full bg-charcoal-900 text-sm font-bold text-white">
                  {step.step}
                </div>
                <h3 className="mt-4 font-heading text-lg font-bold text-charcoal-900">{step.title}</h3>
                <p className="mt-1 text-sm leading-6 text-charcoal-600">{step.description}</p>
              </li>
            ))}
          </ol>
        </LandingContainer>
      </section>

      <section className="bg-white py-10 sm:py-16">
        <LandingContainer>
          <h2 className="font-heading text-2xl font-bold leading-tight tracking-tight text-charcoal-900 sm:text-3xl">
            Included with every form
          </h2>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {FEATURES.map((feature) => (
              <li key={feature.title} className="rounded-2xl border border-neutral-200 bg-white p-4">
                <p className="font-semibold text-charcoal-900">{feature.title}</p>
                <p className="mt-1 text-sm leading-6 text-charcoal-600">{feature.description}</p>
              </li>
            ))}
          </ul>
        </LandingContainer>
      </section>

      <CompareSection />

      <PricingSection />

      <FaqSection items={geoConfig.homepageFaq} />

      {/* CTA */}
      <section className="bg-charcoal-900 py-10 sm:py-20">
        <LandingContainer>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-2xl font-bold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
              Create your first form
            </h2>
            <div className="mt-6 flex items-center justify-center sm:mt-8">
              <Button asChild size="lg" className="min-h-[44px] w-full bg-white text-charcoal-900 hover:bg-neutral-100 sm:w-auto">
                <Link to="/register">
                  Start free
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
            <p className="mt-3 text-sm text-neutral-400">
              No credit card. 150 submissions a month.
            </p>
          </div>
        </LandingContainer>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}